// Anke, 2026-07-18 — text pasted from OUTSIDE the whiteboard must read like the
// text it lands in, not like its source. Word, browsers and Google Docs all put
// fully styled HTML on the clipboard (`<span style="font-size:40px;color:#ff0000
// ;font-weight:700">`), and the textbox schema happily parses every one of those
// into a `textStyle` mark — so a paste used to drop foreign red 40px bold text
// into the middle of a paragraph.
//
// The rule: the pasted characters adopt the formatting of the character BEFORE
// the insertion point. Block structure (paragraphs, bullet and ordered lists) is
// deliberately KEPT — only the inline character formatting is normalised, so a
// list copied off a web page stays a list, just in the textbox's own font.
//
// Text copied from WITHIN the whiteboard keeps its formatting (Anke's choice):
// a deliberately styled run stays styled when moved around the board.
//
// PURE and side-effect free, so both halves are unit-testable without an editor.

import { Fragment, Slice } from '@tiptap/pm/model';
import type { Mark, Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';

/**
 * Whether clipboard HTML was produced by a ProseMirror editor — i.e. copied from
 * a textbox on this board rather than from an outside application.
 *
 * ProseMirror stamps `data-pm-slice` onto the HTML it writes to the clipboard,
 * so its presence is the marker. Caveat: this also matches a copy from a
 * DIFFERENT ProseMirror-based site, which would then keep its formatting even
 * though it is technically external. Accepted — an exact origin check would need
 * our own clipboard serializer, and the failure mode (a rare foreign editor's
 * formatting survives) is mild next to the everyday Word/browser case.
 */
export function isInternalClipboardHtml(html: string): boolean {
  return html.includes('data-pm-slice');
}

/**
 * Re-mark every text node and every other inline leaf (hard breaks) in `slice`
 * with `marks`, leaving block structure untouched. A hard break carries marks
 * like text does — PowerPoint wraps its `<br>` in a styled span, so a pasted
 * line break used to keep a foreign `font-size: 20pt` the optionsbar never
 * shows (Anke, 2026-10-02).
 */
export function adoptMarks(slice: Slice, marks: readonly Mark[]): Slice {
  return new Slice(remarkFragment(slice.content, marks), slice.openStart, slice.openEnd);
}

/**
 * The editor's `handlePaste` prop. Returns `true` when it has handled the paste
 * itself (external source — the slice was re-marked and dispatched), `false` to
 * let ProseMirror paste normally (internal source — formatting is preserved).
 *
 * Lives here rather than inline at the `new Editor(...)` call so the real
 * handler — not a copy of it — can be driven from a test.
 */
export function handleTextboxPaste(
  view: EditorView,
  event: ClipboardEvent,
  slice: Slice,
): boolean {
  const html = event.clipboardData?.getData('text/html') ?? '';
  if (isInternalClipboardHtml(html)) return false;

  view.dispatch(
    view.state.tr.replaceSelection(adoptMarks(slice, insertionMarks(view))).scrollIntoView(),
  );
  return true;
}

/**
 * The formatting pasted text should adopt, in precedence order:
 *
 *  1. `storedMarks` — the user just toggled a format for the next thing they
 *     type, and a paste IS that next thing.
 *  2. A collapsed caret: `$from.marks()`, literally "the marks of the character
 *     before this position" — the rule as Anke stated it. (At the very start of
 *     a textblock ProseMirror reads forward instead, which is the sane answer
 *     when there is no preceding character.)
 *  3. A selection being replaced: `marksAcross`, the marks that hold across the
 *     whole replaced range.
 *  4. The first text run inside the replaced range. Needed for SELECT-ALL, where
 *     both 2 and 3 come up empty: an `AllSelection` starts at the doc level, not
 *     inside a paragraph, so there is neither a "character before" nor a
 *     textblock for `marksAcross` to work from. Without this, selecting all the
 *     text and pasting silently stripped the box back to its bare defaults.
 */
function insertionMarks(view: EditorView): readonly Mark[] {
  const { storedMarks, selection } = view.state;
  if (storedMarks) return storedMarks;
  if (selection.empty) return selection.$from.marks();
  const across = selection.$from.marksAcross(selection.$to);
  if (across && across.length > 0) return across;
  return firstTextMarksIn(view, selection.from, selection.to) ?? selection.$from.marks();
}

/** Marks of the first text run in `[from, to)`, or `null` if the range has none. */
function firstTextMarksIn(view: EditorView, from: number, to: number): readonly Mark[] | null {
  let marks: readonly Mark[] | null = null;
  view.state.doc.nodesBetween(from, to, (node) => {
    if (marks === null && node.isText) marks = node.marks;
  });
  return marks;
}

function remarkFragment(fragment: Fragment, marks: readonly Mark[]): Fragment {
  const out: ProseMirrorNode[] = [];
  fragment.forEach((node) => {
    if (node.isInline) {
      // Text and hard breaks alike. `mark()` REPLACES the node's mark set, which
      // is the point: bold/italic and any foreign textStyle attrs go, the
      // insertion point's set comes.
      out.push(node.mark(marks as Mark[]));
    } else if (node.content.size > 0) {
      out.push(node.copy(remarkFragment(node.content, marks)));
    } else {
      out.push(node);
    }
  });
  return Fragment.fromArray(out);
}
